import React, { useEffect, useState } from 'react';
import {
    ApiResponse, AudioCliente,
    ESTADO_AUDIO_LABELS, ESTADO_AUDIO_COLORS,
    URGENCIA_AUDIO_LABELS, URGENCIA_AUDIO_COLORS,
    INTENCION_AUDIO_LABELS
} from '../types';
import { toast, confirmToast } from '../lib/toast';
import { apiFetch } from '../lib/apiFetch';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

interface Props {
    audio: AudioCliente;
    onClose: () => void;
    onActualizado: () => void;
}

/**
 * Llama a un endpoint de acción (aprobar/crear-tarea/descartar/reprocesar)
 * y maneja el 409 (conflicto de versión) mostrando el aviso que pide el
 * documento. El 404 queda como red de contención por si el registro se
 * borró entre que se cargó la lista y que se hizo clic. Devuelve el body
 * de la respuesta (para poder encadenar acciones con el `data` ya
 * actualizado, ver `handleAprobar`) o `null` si falló.
 */
async function llamarAccion(path: string, body: object): Promise<ApiResponse<AudioCliente> | null> {
    try {
        const res = await apiFetch(`${API_BASE}${path}`, {
            method: path.includes('crear-tarea') ? 'POST' : 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        });

        if (res.status === 409) {
            toast.warning('El registro fue modificado, recargá');
            return null;
        }
        if (res.status === 404) {
            toast.error('El audio ya no existe (puede haber sido descartado)');
            return null;
        }

        const json: ApiResponse<AudioCliente> = await res.json();
        if (!json.success) {
            toast.error(json.error || 'Error al procesar la acción');
            return null;
        }
        return json;
    } catch (e) {
        toast.error('No se pudo conectar con el servidor');
        return null;
    }
}

export const AudioDetalleModal: React.FC<Props> = ({ audio: audioInicial, onClose, onActualizado }) => {
    // audioInicial viene de la fila de la lista; acá se refresca contra
    // GET /api/audios/:id para asegurar el detalle completo y al día
    // (transcripción, análisis) sin depender de qué haya traído la lista.
    const [audio, setAudio] = useState(audioInicial);
    const [respuestaFinal, setRespuestaFinal] = useState(audioInicial.analisis?.respuesta_sugerida || '');
    const [procesando, setProcesando] = useState(false);
    const [audioUrl, setAudioUrl] = useState<string | null>(null);

    useEffect(() => {
        let cancelado = false;
        apiFetch(`${API_BASE}/audios/${audioInicial.id}`)
            .then(res => res.json())
            .then((json: ApiResponse<AudioCliente>) => {
                if (!cancelado && json.success && json.data) {
                    setAudio(json.data);
                    if (json.data.analisis) setRespuestaFinal(json.data.analisis.respuesta_sugerida);
                }
            })
            .catch(() => { /* se queda con audioInicial si falla */ });
        return () => { cancelado = true; };
    }, [audioInicial.id]);

    useEffect(() => {
        // El <audio src="..."> del navegador no puede llevar el header
        // Authorization, así que se trae el archivo como blob autenticado.
        let cancelado = false;
        let objectUrl: string | null = null;
        apiFetch(`${API_BASE}/audios/${audio.id}/archivo`)
            .then(res => (res.ok ? res.blob() : null))
            .then(blob => {
                if (cancelado || !blob) return;
                objectUrl = URL.createObjectURL(blob);
                setAudioUrl(objectUrl);
            })
            .catch(() => { /* el player se queda vacío si falla */ });
        return () => {
            cancelado = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [audio.id]);

    // 2026-09 — antes "Aprobar" y "Crear tarea" eran dos pasos separados: se
    // podía aprobar la respuesta y cerrar el modal sin que la tarea llegara
    // nunca al tablero (había que acordarse de volver a abrir el audio y
    // tocar "Crear tarea" aparte). Ahora aprobar intenta crear la tarea en
    // el mismo paso — así "aprobado" siempre implica "está en el tablero",
    // para llevar el orden y control que se necesita. Si el audio ya tenía
    // una tarea (se había creado antes a mano), no se duplica: se detecta
    // acá mismo y se saltea, sin generar el error de "ya tiene tarea" que
    // devolvería el endpoint si se llamara igual.
    const handleAprobar = async () => {
        setProcesando(true);
        const resultado = await llamarAccion(`/audios/${audio.id}/aprobar`, {
            respuesta_final: respuestaFinal,
            version: audio.version
        });
        if (resultado) {
            const actualizado = resultado.data;
            if (actualizado && !actualizado.analisis?.tarea_id) {
                const resultadoTarea = await llamarAccion(`/audios/${audio.id}/crear-tarea`, {
                    version: actualizado.version
                });
                toast.success(
                    resultadoTarea
                        ? 'Respuesta aprobada y tarea creada en el tablero'
                        : 'Respuesta aprobada — no se pudo crear la tarea sola, usá "Crear tarea" a mano'
                );
            } else {
                toast.success('Respuesta aprobada');
            }
            onActualizado();
            onClose();
        }
        setProcesando(false);
    };

    const handleCrearTarea = async () => {
        setProcesando(true);
        const resultado = await llamarAccion(`/audios/${audio.id}/crear-tarea`, { version: audio.version });
        setProcesando(false);
        if (resultado) {
            toast.success('Tarea creada');
            onActualizado();
            onClose();
        }
    };

    const handleDescartar = async () => {
        const confirmado = await confirmToast({
            title: '¿Descartar este audio?',
            description: 'No se va a poder deshacer.',
            type: 'warning'
        });
        if (!confirmado) return;

        setProcesando(true);
        const resultado = await llamarAccion(`/audios/${audio.id}/descartar`, { version: audio.version });
        setProcesando(false);
        if (resultado) {
            toast.success('Audio descartado');
            onActualizado();
            onClose();
        }
    };

    // A diferencia de "Descartar" (soft delete, `llamarAccion`/PATCH, y solo
    // disponible cuando ya hay análisis), esto es un borrado permanente
    // (fila + archivo) y está disponible siempre — para poder sacarse de
    // encima audios cargados por error en cualquier estado.
    const handleEliminar = async () => {
        const confirmado = await confirmToast({
            title: '¿Eliminar este audio?',
            description: 'Se borra el registro y el archivo de forma permanente. No se puede deshacer.',
            confirmText: 'Eliminar',
            type: 'warning'
        });
        if (!confirmado) return;

        setProcesando(true);
        try {
            const res = await apiFetch(`${API_BASE}/audios/${audio.id}`, { method: 'DELETE' });
            if (res.status === 404) {
                toast.error('El audio ya no existe');
                onActualizado();
                onClose();
                return;
            }
            const json: ApiResponse<unknown> = await res.json();
            if (!json.success) {
                toast.error(json.error || 'Error al eliminar el audio');
                return;
            }
            toast.success('Audio eliminado');
            onActualizado();
            onClose();
        } catch (e) {
            toast.error('No se pudo conectar con el servidor');
        } finally {
            setProcesando(false);
        }
    };

    const handleReprocesar = async () => {
        setProcesando(true);
        const resultado = await llamarAccion(`/audios/${audio.id}/reprocesar`, { version: audio.version });
        setProcesando(false);
        if (resultado) {
            toast.success('Audio en cola para reprocesar');
            onActualizado();
            onClose();
        }
    };

    const puedeAprobar = audio.estado === 'pendiente_revision' && audio.analisis;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <div className="flex items-center gap-2">
                            <div className="w-1 h-4 rounded-full" style={{ backgroundColor: audio.cliente_color }} />
                            <h3 className="text-base font-semibold text-slate-800">{audio.empresa}</h3>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${ESTADO_AUDIO_COLORS[audio.estado]}`}>
                                {ESTADO_AUDIO_LABELS[audio.estado]}
                            </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">{audio.cliente_nombre}</p>
                        {audio.tarea_id && (
                            <span className="inline-block mt-1 text-[11px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                                🗂️ Tarea #{audio.tarea_id} en el tablero
                            </span>
                        )}
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
                </div>

                {audioUrl ? (
                    <audio controls className="w-full mb-4" src={audioUrl}>
                        Tu navegador no soporta el elemento de audio.
                    </audio>
                ) : (
                    <div className="w-full mb-4 h-10 flex items-center text-xs text-slate-500">Cargando audio...</div>
                )}

                {audio.estado === 'error' && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <strong>Error al procesar:</strong> {audio.error_detalle}
                            </div>
                            <button
                                onClick={handleReprocesar}
                                disabled={procesando}
                                className="shrink-0 px-3 py-1.5 text-xs bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50"
                            >
                                Reprocesar
                            </button>
                        </div>
                    </div>
                )}

                {['recibido', 'transcribiendo', 'analizando'].includes(audio.estado) && (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                        Procesando... esto se actualiza solo cada 10s.
                    </div>
                )}

                {audio.analisis && (
                    <>
                        <div className="mb-4">
                            <label className="block text-xs font-medium text-slate-500 mb-1">Transcripción</label>
                            <div className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-3 max-h-56 overflow-y-auto">
                                {audio.analisis.transcripcion}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <div>
                                <label className="block text-xs font-medium text-slate-500 mb-1">Intención</label>
                                <div className="text-sm">{INTENCION_AUDIO_LABELS[audio.analisis.intencion]}</div>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-slate-500 mb-1">Urgencia</label>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${URGENCIA_AUDIO_COLORS[audio.analisis.urgencia]}`}>
                                    {URGENCIA_AUDIO_LABELS[audio.analisis.urgencia]}
                                </span>
                            </div>
                            <div className="col-span-2">
                                <label className="block text-xs font-medium text-slate-500 mb-1">Sistema afectado</label>
                                <div className="text-sm text-slate-700">{audio.analisis.sistema_afectado || '—'}</div>
                            </div>
                            <div className="col-span-2">
                                <label className="block text-xs font-medium text-slate-500 mb-1">Resumen</label>
                                <div className="text-sm text-slate-700">{audio.analisis.resumen}</div>
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="block text-xs font-medium text-slate-500 mb-1">Respuesta al cliente (editable)</label>
                            <textarea
                                value={respuestaFinal}
                                onChange={e => setRespuestaFinal(e.target.value)}
                                rows={5}
                                className="w-full text-sm border border-slate-300 rounded-lg p-3"
                            />
                        </div>

                        <div className="flex flex-wrap gap-2 justify-end">
                            <button
                                onClick={handleDescartar}
                                disabled={procesando}
                                className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                            >
                                Descartar
                            </button>
                            {/* Desde que la tarea se crea sola al subir el audio, este botón
                                solo sirve de respaldo para audios viejos que quedaron sin una
                                (de antes de este cambio, o si falló la creación automática). */}
                            {!audio.tarea_id && (
                                <button
                                    onClick={handleCrearTarea}
                                    disabled={procesando}
                                    className="px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg disabled:opacity-50"
                                >
                                    Crear tarea
                                </button>
                            )}
                            <button
                                onClick={handleAprobar}
                                disabled={procesando || !puedeAprobar}
                                className="px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg disabled:opacity-50"
                            >
                                Aprobar respuesta
                            </button>
                        </div>
                    </>
                )}

                <div className="flex justify-end pt-4 mt-2 border-t border-slate-100">
                    <button
                        onClick={handleEliminar}
                        disabled={procesando}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                        title="Eliminar de forma permanente (para audios cargados por error)"
                    >
                        🗑️ Eliminar audio
                    </button>
                </div>
            </div>
        </div>
    );
};
